// LeadTimePricingTable.jsx
import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
} from "@mui/material";
import { styled } from "@mui/system";

const StyledTableContainer = styled(TableContainer)({
  boxShadow: "none",
  borderRadius: "0px",
  marginTop: "20px",
  border: "1px solid #E0E0E0",
  overflow: "hidden",
});

const StyledTableHead = styled(TableHead)({
  backgroundColor: "#FFFFFF",
});

const StyledHeaderCell = styled(TableCell)({
  fontFamily: "Roboto",
  fontWeight: "500",
  fontSize: "14px",
  color: "#163A90",
  borderBottom: "1px solid #E0E0E0",
  whiteSpace: "nowrap",
  padding: "12px",
});

const StyledTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? "#d6d6d6" : "#d6e2f2",
}));

const StyledTableCell = styled(TableCell)({
  fontFamily: "Roboto",
  fontSize: "14px",
  color: "#000000",
  padding: "5px",
  borderBottom: "1px solid #E0E0E0",
});

const LeadTimePricingTable = ({ data }) => {
  // Format date to show day and date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.toLocaleDateString("en-US", { weekday: "short" });
    const dateStr = date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
    });
    return { day, dateStr };
  };

  // Get competitor names from the first entry
  const getCompetitorNames = () => {
    if (!data || !Array.isArray(data) || data.length === 0) return [];

    const firstValidItem = data.find(
      (item) => item.compSet && item.compSet.length > 0,
    );
    if (!firstValidItem) return [];

    return firstValidItem.compSet.map((comp) => ({
      id: comp.competitorID,
      name: comp.competitorName,
    }));
  };

  const competitors = getCompetitorNames();

  // Format price value
  const formatPrice = (price) => {
    if (price === null || price === undefined || price === -1) return "-";
    if (price === 0) return "Sold Out";
    if (price === -999) return "N/A";
    return price;
  };

  // Format occupancy
  const formatOccupancy = (occupancy) => {
    if (occupancy === null || occupancy === undefined || occupancy === -1)
      return "-";
    return `${Math.round(occupancy)} %`;
  };

  // Format BOB (Books on Books)
  const formatBOB = (bob) => {
    if (bob === null || bob === undefined || bob === -1) return "-";
    return bob;
  };

  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          padding: "20px",
          textAlign: "center",
          fontFamily: "Roboto",
        }}
      >
        No data available
      </Box>
    );
  }

  // Filter out invalid entries and reverse for chronological order
  const validData = data.filter((item) => item.leadTime >= 0);

  return (
    <StyledTableContainer>
      <Table
        sx={{ minWidth: 650, height: "100%" }}
        aria-label="lead time pricing table"
      >
        <StyledTableHead>
          <TableRow>
            <StyledHeaderCell>As Of</StyledHeaderCell>
            <StyledHeaderCell align="center">Lead Time</StyledHeaderCell>
            <StyledHeaderCell align="center">BOB</StyledHeaderCell>
            <StyledHeaderCell align="center">Occupancy %</StyledHeaderCell>
            <StyledHeaderCell align="center">Current Price</StyledHeaderCell>
            <StyledHeaderCell align="center">Calculated Price</StyledHeaderCell>
            {competitors.map((comp) => (
              <StyledHeaderCell key={comp.id} align="center">
                {comp.name}
              </StyledHeaderCell>
            ))}
          </TableRow>
        </StyledTableHead>
        <TableBody>
          {validData.map((row, index) => {
            const { day, dateStr } = formatDate(row.date);
            return (
              <StyledTableRow key={row.date} index={index}>
                <StyledTableCell component="th" scope="row">
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <span>{dateStr}</span>
                    <span style={{ fontWeight: 500 }}>{day}</span>
                  </Box>
                </StyledTableCell>
                <StyledTableCell align="center">{row.leadTime}</StyledTableCell>
                <StyledTableCell align="center">
                  {formatBOB(row.bob)}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {formatOccupancy(row.occupancyPercentage)}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {formatPrice(row.currentPrice)}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {formatPrice(row.systemPrice)}
                </StyledTableCell>
                {row.compSet &&
                  row.compSet.map((comp) => (
                    <StyledTableCell key={comp.competitorID} align="center">
                      {formatPrice(comp.price)}
                    </StyledTableCell>
                  ))}
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export default LeadTimePricingTable;
